import React, {useEffect, useState} from "react";
import {Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle} from "@mui/material";
import {
  ATTENTION_LABELS,
  DEVICE_TYPES,
  TYPE_PLURALS,
  hasActiveFilters,
  onlineBadge,
  onlinePercent,
  typedNameMatches,
} from "./consoleModel";

const fmt = (n) => (n == null ? "–" : Number(n).toLocaleString("en-IN"));

// ---------------------------------------------------------------- pills

const PILL = "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap";
const TONES = {
  on: "bg-success-50 text-success-700",
  off: "bg-danger-50 text-danger-700",
  warn: "bg-warning-50 text-warning-800",
  none: "bg-ink-100 text-ink-600",
};

export function Pill({tone = "none", dot = false, children}) {
  return (
    <span className={`${PILL} ${TONES[tone] || TONES.none}`}>
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />}
      {children}
    </span>
  );
}

export function OnlinePill({device}) {
  const {tone, label} = onlineBadge(device);
  return (
    <Pill tone={tone} dot={tone !== "none" || label === "Off duty"}>
      {label}
    </Pill>
  );
}

export function ClientStatusPill({status}) {
  return status === "ACTIVE" ? <Pill tone="on">Active</Pill> : <Pill>Inactive</Pill>;
}

export function AttentionPill({code}) {
  if (!code) return null;
  return (
    <Pill tone={code === "NOTHING_ONLINE" ? "off" : "warn"} dot>
      {ATTENTION_LABELS[code] || code}
    </Pill>
  );
}

export function OnlineBar({row}) {
  if (!row?.liveStatusDevices) return <span className="text-ink-400">–</span>;
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap tabular-nums">
      {fmt(row.online)} / {fmt(row.liveStatusDevices)}
      <span className="h-1.5 w-14 rounded-full bg-ink-100 overflow-hidden" aria-hidden="true">
        <span className="block h-full bg-success-500" style={{width: `${onlinePercent(row)}%`}} />
      </span>
    </span>
  );
}

// ---------------------------------------------------------------- overview strip

function Tile({label, value, sub, tone, onClick}) {
  const valueTone = {ok: "text-success-700", bad: "text-danger-700", warn: "text-warning-700"}[tone] || "text-ink-900";
  return (
    <button
      type="button"
      onClick={onClick}
      title="Show these"
      className="text-left min-w-0 px-4 py-3 bg-white border border-ink-200 rounded-xl hover:border-brand-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 transition-colors"
    >
      <span className="block text-[11px] font-semibold uppercase tracking-wider text-ink-500">{label}</span>
      <span className={`block text-2xl font-bold tabular-nums leading-tight ${valueTone}`}>{fmt(value)}</span>
      <span className="block text-xs text-ink-500 truncate">{sub}</span>
    </button>
  );
}

/**
 * System-wide counts (overview) or one client's (row). Every count opens the matching list:
 * onJump({kind: "clients" | "devices" | "online" | "offline" | "attention" | "users" | "type", value}).
 */
export function OverviewStrip({overview, row, onJump}) {
  if (!overview && !row) {
    return <div className="h-[92px] rounded-xl bg-ink-100 animate-pulse" aria-hidden="true" />;
  }
  const inClient = !!row;
  const devices = inClient ? row.devices : overview.devices;
  const online = inClient ? row.online : overview.online;
  const offline = inClient ? row.offline : overview.offline;
  const live = inClient ? row.liveStatusDevices : overview.devices - overview.noLiveStatus;
  const suspended = inClient ? row.suspended : overview.suspended;
  const byType = inClient
    ? Object.fromEntries(DEVICE_TYPES.map((t) => [t, {total: row.devicesByType?.[t] || 0, online: null}]))
    : overview.byType || {};

  return (
    <div className="flex flex-col gap-2">
      <div className="grid gap-2" style={{gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))"}}>
        {!inClient && (
          <Tile
            label="Clients"
            value={overview.clients}
            sub={`${fmt(overview.activeClients)} active · ${fmt(overview.inactiveClients)} inactive`}
            onClick={() => onJump({kind: "clients"})}
          />
        )}
        <Tile
          label="Devices"
          value={devices}
          sub={suspended ? `${fmt(suspended)} suspended` : inClient ? "registered to this client" : "across all clients"}
          onClick={() => onJump({kind: "devices"})}
        />
        <Tile
          label="Online"
          value={online}
          tone="ok"
          sub={live ? `${Math.round((online * 100) / live)}% of devices with live status` : "no devices with live status"}
          onClick={() => onJump({kind: "online"})}
        />
        <Tile label="Offline" value={offline} tone="bad" sub="devices with live status" onClick={() => onJump({kind: "offline"})} />
        {inClient ? (
          <Tile label="Users" value={row.users} sub="team members and drivers" onClick={() => onJump({kind: "users"})} />
        ) : (
          <Tile
            label="Needs attention"
            value={overview.clientsNeedingAttention}
            tone="warn"
            sub={overview.clientsNeedingAttention === 1 ? "client" : "clients"}
            onClick={() => onJump({kind: "attention"})}
          />
        )}
      </div>
      <div className="grid gap-2" style={{gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))"}}>
        {DEVICE_TYPES.filter((t) => !inClient || byType[t]?.total).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => onJump({kind: "type", value: t})}
            title="Show these"
            className="flex flex-wrap items-baseline gap-x-2 px-3 py-2 rounded-lg bg-ink-50 border border-transparent hover:border-brand-400 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
          >
            <span className="text-sm font-semibold text-ink-900">{TYPE_PLURALS[t]}</span>
            <span className="text-sm font-bold tabular-nums text-ink-900">{fmt(byType[t]?.total || 0)}</span>
            {!inClient && (
              <span className="text-xs text-ink-500">
                {t === "BT_LOCK" ? "last report only" : `${fmt(byType[t]?.online || 0)} online`}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- filter bar

const FIELD_LABEL = "flex flex-col gap-1 text-[11px] font-semibold uppercase tracking-wider text-ink-500 min-w-0";
const CONTROL =
  "h-9 px-2.5 rounded-md border border-ink-300 bg-white text-sm font-medium normal-case tracking-normal text-ink-900 focus:outline-none focus:ring-2 focus:ring-brand-400";

/** Search box that waits for typing to pause before filtering. */
function SearchField({id, label, value, placeholder, onChange}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  useEffect(() => {
    if (draft === value) return undefined;
    const timer = setTimeout(() => onChange(draft), 300);
    return () => clearTimeout(timer);
  }, [draft]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <label className={`${FIELD_LABEL} flex-1 basis-56`} htmlFor={id}>
      {label}
      <input
        id={id}
        type="search"
        className={CONTROL}
        value={draft}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => setDraft(e.target.value)}
      />
    </label>
  );
}

/**
 * A row of filters: one search field plus dropdowns.
 * fields: [{key, label, options: [{value, label}]}]; filters: current values; onChange(patch).
 */
export function FilterBar({idPrefix, scope, filters, search, fields, onChange, onReset}) {
  return (
    <div className="flex flex-wrap items-end gap-3 px-4 py-3 border-b border-ink-200">
      <SearchField
        id={`${idPrefix}-q`}
        label="Search"
        value={filters.q}
        placeholder={search}
        onChange={(q) => onChange({q})}
      />
      {fields.map((field) => (
        <label key={field.key} className={FIELD_LABEL} htmlFor={`${idPrefix}-${field.key}`}>
          {field.label}
          <select
            id={`${idPrefix}-${field.key}`}
            className={`${CONTROL} max-w-[15rem] cursor-pointer`}
            value={String(filters[field.key])}
            onChange={(e) => onChange({[field.key]: e.target.value})}
          >
            {field.options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      ))}
      <button
        type="button"
        onClick={onReset}
        disabled={!hasActiveFilters(scope, filters)}
        className="h-9 px-3 text-sm font-semibold rounded-md text-brand-700 hover:bg-brand-50 disabled:opacity-40 disabled:hover:bg-transparent"
      >
        Reset filters
      </button>
    </div>
  );
}

// ---------------------------------------------------------------- navigation

export function ViewSwitch({view, clients, devices, onChange}) {
  const tab = (key, label, count) => (
    <button
      key={key}
      type="button"
      role="tab"
      aria-selected={view === key}
      onClick={() => onChange(key)}
      className={`h-8 px-3.5 rounded-md text-sm font-semibold transition-colors ${
        view === key ? "bg-white text-ink-900 shadow-sm" : "text-ink-500 hover:text-ink-800"
      }`}
    >
      {label} <span className="ml-1 font-medium tabular-nums text-ink-500">{fmt(count)}</span>
    </button>
  );
  return (
    <div role="tablist" aria-label="View" className="inline-flex p-1 rounded-lg bg-ink-100 border border-ink-200">
      {tab("clients", "Clients", clients)}
      {tab("devices", "All devices", devices)}
    </div>
  );
}

export function Breadcrumb({backLabel, current, onBack}) {
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-ink-500">
      <button type="button" onClick={onBack} className="font-semibold text-brand-700 underline underline-offset-2">
        {backLabel}
      </button>
      <span aria-hidden="true">›</span>
      <span className="font-semibold text-ink-900">{current}</span>
    </nav>
  );
}

export function AccessDenied() {
  return (
    <div className="max-w-xl mx-auto mt-16 p-6 bg-white border border-ink-200 rounded-xl text-center">
      <h2 className="text-lg font-semibold text-ink-900">You don't have access to this page</h2>
      <p className="mt-2 text-sm text-ink-600">
        The super admin console is only for Yekkada super admins. Sign in with a super admin account, or go back to
        your dashboard.
      </p>
    </div>
  );
}

export function LoadError({message, onRetry}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-danger-100 bg-danger-50 text-sm text-danger-800">
      <span>{message}</span>
      <button type="button" onClick={onRetry} className="font-semibold underline">
        Try again
      </button>
    </div>
  );
}

/** In-page confirm step for every change the console makes. */
export function ConfirmDialog({request, busy, onCancel, onConfirm}) {
  // request.typeToConfirm (optional): a name that must be typed before the change can be confirmed
  // (Delete client). Reset whenever a new request opens.
  const [typed, setTyped] = useState("");
  useEffect(() => setTyped(""), [request]);
  const needsName = !!request?.typeToConfirm;
  const nameOk = !needsName || typedNameMatches(typed, request.typeToConfirm);
  return (
    <Dialog open={!!request} onClose={busy ? undefined : onCancel} maxWidth="xs" fullWidth>
      {request && (
        <>
          <DialogTitle>{request.title}</DialogTitle>
          <DialogContent>
            <DialogContentText>{request.message}</DialogContentText>
            {needsName && (
              <label className="block mt-4 text-sm text-ink-700">
                Type <span className="font-semibold text-ink-900">{request.typeToConfirm}</span> to confirm
                <input
                  type="text"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && nameOk && !busy && onConfirm()}
                  disabled={busy}
                  autoComplete="off"
                  autoFocus
                  aria-label="Client name"
                  className="mt-1 block w-full h-9 px-3 text-sm rounded-md border border-solid border-ink-300 focus:border-danger-600 focus:outline-none focus-visible:ring-0"
                />
              </label>
            )}
          </DialogContent>
          <DialogActions>
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="h-9 px-3 text-sm font-semibold rounded-md text-ink-700 hover:bg-ink-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={busy || !nameOk}
              className={`h-9 px-4 text-sm font-semibold rounded-md text-white disabled:opacity-60 ${
                request.danger ? "bg-danger-600 hover:bg-danger-700" : "bg-brand-700 hover:bg-brand-800"
              }`}
            >
              {busy ? "Saving…" : request.confirmLabel}
            </button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
